import { Component, OnDestroy, inject, OnInit } from '@angular/core';
import {
  DomSanitizer,
  SafeResourceUrl,
  SafeUrl,
} from '@angular/platform-browser';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { CommonModule } from '@angular/common';
import { ContentsService } from '@nimmsta/ng-commons-lib/services/contents';
import { LoadingIndicatorComponent } from '@nimmsta/ng-commons-lib/loading-indicator';
import {
  classifyWorkflowMediaPreview,
  WorkflowMediaDisplayItem,
  WorkflowMediaPreview,
  WorkflowMediaPreviewType,
} from './workflow-media-preview.util';

@Component({
  selector: 'app-show-workflow',
  templateUrl: './show-workflow.component.html',
  imports: [CommonModule, TranslatePipe, LoadingIndicatorComponent],
})
export class ShowWorkflowComponent implements OnInit, OnDestroy {
  private readonly dialogRef = inject(DynamicDialogRef);
  private readonly dialogConfig = inject(
    DynamicDialogConfig<{ workflow: WorkflowMediaDisplayItem }>,
  );
  private sanitizer = inject(DomSanitizer);
  private translate = inject(TranslateService);
  public contentsService = inject(ContentsService);

  workflow?: WorkflowMediaDisplayItem;
  contentType?: WorkflowMediaPreviewType;
  safeResourceUrl?: SafeResourceUrl;
  imageUrl?: SafeUrl;
  htmlContent?: string;
  isLoading = true;
  private htmlObjectUrl?: string;

  ngOnInit(): void {
    this.workflow = this.dialogConfig.data?.workflow ?? this.workflow;
    this.processWorkflowContent();
  }

  ngOnDestroy(): void {
    this.clearHtmlObjectUrl();
  }

  private processWorkflowContent(): void {
    if (!this.workflow) {
      this.isLoading = false;
      return;
    }
    if (this.workflow.embedString?.trim()) {
      this.applyPreview(
        classifyWorkflowMediaPreview({
          embedString: this.workflow.embedString,
        }),
      );
      this.isLoading = false;
    } else if (this.workflow.fileKey) {
      this.contentsService
        .getSignedUrl(this.workflow.fileKey)
        .subscribe((signedUrl: string) => {
          this.applyPreview(
            classifyWorkflowMediaPreview({
              fileUrl: signedUrl,
              fileKey: this.workflow?.fileKey,
            }),
          );
          this.isLoading = false;
        });
    } else {
      this.isLoading = false;
    }
  }

  private applyPreview(preview: WorkflowMediaPreview | null): void {
    this.clearHtmlObjectUrl();

    this.contentType = undefined;
    this.safeResourceUrl = undefined;
    this.imageUrl = undefined;
    this.htmlContent = undefined;
    if (!preview) {
      return;
    }

    this.contentType = preview.type;
    if (preview.type === 'image' && preview.src) {
      this.imageUrl = this.sanitizer.bypassSecurityTrustUrl(preview.src);
      return;
    }

    if ((preview.type === 'iframe' || preview.type === 'pdf') && preview.src) {
      this.safeResourceUrl = this.sanitizer.bypassSecurityTrustResourceUrl(
        preview.src,
      );
      return;
    }

    if (preview.type === 'html' && preview.srcDoc) {
      this.htmlObjectUrl = URL.createObjectURL(
        new Blob([preview.srcDoc], { type: 'text/html' }),
      );
      this.safeResourceUrl = this.sanitizer.bypassSecurityTrustResourceUrl(
        this.htmlObjectUrl,
      );
    }
  }

  close(): void {
    this.dialogRef.close();
  }

  getTitle(): string {
    return this.workflow?.name || this.translate.instant('workflow.show');
  }

  private clearHtmlObjectUrl(): void {
    if (this.htmlObjectUrl) {
      URL.revokeObjectURL(this.htmlObjectUrl);
      this.htmlObjectUrl = undefined;
    }
  }
}
